import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HiArrowRight } from "react-icons/hi";
import { PageHeader, Reveal } from "../components/ui";

const domains = [
  {
    id: "rtl-architecture",
    title: "RTL & Architecture",
    summary: "Micro-architecture, RTL development, IP integration, CDC/lint and low-power design.",
    items: ["RTL design", "Analog and mixed signal"],
  },
  {
    id: "design-verification",
    title: "Design Verification",
    summary: "UVM, formal/ABV, coverage closure, regression and verification infrastructure.",
    items: ["Design verification", "Verification and validation"],
  },
  {
    id: "dft",
    title: "DFT",
    summary: "Scan/ATPG, MBIST/LBIST, test architecture, signoff and silicon debug.",
    items: ["DFT", "Test and Product Engineering"],
  },
  {
    id: "physical-design-sta",
    title: "Physical Design & STA",
    summary: "Synthesis, floorplan through routing, timing closure, physical verification and signoff.",
    items: ["Physical design", "Synthesis and STA", "Design signoff"],
  },
  {
    id: "fpga-emulation",
    title: "FPGA & Emulation",
    summary: "FPGA design, prototyping, emulation and pre-silicon validation.",
    items: ["FPGA design", "Prototyping and emulation"],
  },
  {
    id: "silicon-embedded",
    title: "Silicon & Embedded",
    summary: "Bring-up, characterization, product/test engineering, firmware, drivers and BSP.",
    items: [
      "Bench characterization",
      "Silicon validation",
      "Silicon validation lab access",
      "Bare metal programming",
      "Board support package",
      "Device drivers",
      "OS porting and customization",
      "Diagnostics",
      "CI/CD",
      "Cyber security",
      "Product solutions",
    ],
  },
];

const models = [
  { title: "Defined SOW", body: "A scoped engineering team, named deliverables, governance and measurable acceptance criteria." },
  { title: "Specialist augmentation", body: "Domain-screened engineers integrated into your tools, cadence and technical leadership." },
  { title: "Build–Operate–Transfer", body: "Launch and stabilize an engineering team, then transfer it against an agreed operating plan." },
];

const DESCRIPTION =
  "Explore Chiparama's semiconductor capabilities—from RTL and verification to DFT, physical design, signoff, silicon validation and embedded software.";

export default function ServicePage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const t = setTimeout(
        () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }),
        100
      );
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>Capabilities | Semiconductor Design, Verification & Embedded | Chiparama</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://chiparama.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Capabilities | Semiconductor Design, Verification & Embedded | Chiparama" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://chiparama.com/services" />
        <meta property="og:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Capabilities | Semiconductor Design, Verification & Embedded | Chiparama" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Chiparama",
            url: "https://chiparama.com/services",
            image: "https://chiparama.com/chiparamalogo.jpg",
            provider: { "@type": "Organization", name: "Chiparama", url: "https://chiparama.com" },
            serviceType: domains.map((d) => d.title),
          })}
        </script>
      </Helmet>

      <PageHeader
        eyebrow="Capabilities"
        title="Semiconductor execution across the lifecycle."
        actions={
          <>
            <Link to="/contact" className="btn-primary px-7 py-3.5">Discuss your program</Link>
            <a href="#engagement-models" className="btn-outline-dark px-7 py-3.5">Engagement models</a>
          </>
        }
      >
        From front-end design and verification to physical implementation, signoff, validation and
        embedded software, Chiparama builds teams around the work package. Start with a defined
        milestone or extend an existing team with domain-proven engineers.
      </PageHeader>

      <section className="section relative overflow-hidden bg-white">
        <div className="bg-grid-light absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent_40%)]" aria-hidden="true" />
        <div className="container-x relative">
          <div className="border-y border-slate-200">
            {domains.map((d, i) => (
              <Reveal key={d.id}>
                <div id={d.id} className="grid scroll-mt-24 gap-8 border-b border-slate-200 py-14 last:border-b-0 md:grid-cols-[6rem_1fr_1.1fr] md:gap-12 md:py-20">
                  <span className="font-display text-6xl font-semibold tracking-tighter text-slate-200 md:text-7xl">0{i + 1}</span>
                  <div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight text-ink md:text-4xl">{d.title}</h2>
                    <p className="mt-4 text-lg leading-relaxed text-slate-600">{d.summary}</p>
                    <Link to="/contact" className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                      Discuss a {d.title} requirement
                      <HiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                  <ul className="flex flex-wrap content-start gap-2.5">
                    {d.items.map((item) => (
                      <li key={item} className="rounded-full border border-slate-200 bg-paper px-4 py-2 text-sm text-slate-700 transition-colors hover:border-brand/40 hover:bg-brand-tint hover:text-brand">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="engagement-models" className="on-dark noise section relative scroll-mt-20 overflow-hidden bg-navy-900 text-white">
        <div className="container-x">
          <p className="eyebrow mb-4">Engagement models</p>
          <h2 className="h-section max-w-3xl">Choose the model around the milestone.</h2>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {models.map((m) => (
              <div key={m.title} className="card-dark">
                <h3 className="text-xl font-semibold text-white">{m.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-300">{m.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-slate-400">
            Building a startup team instead?{" "}
            <Link to="/startup-workforce" className="text-brand-light underline underline-offset-4 hover:text-white">
              Explore Startup Workforce
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}
